// What a refund does to the purchase — the partner's decision.
//
// It is their money, their buyers and their terms, so this is a setting
// rather than a behaviour baked into the code.
//
// WHY IT DEFAULTS TO "NO REFUNDS"
//
// Same reasoning as the test/live payment switch: reversing a payment is
// consequential and should be something somebody chose, not something
// they inherited.
//
// WHAT THE PANEL HAS TO BE HONEST ABOUT
//
// Two of the four options un-reveal pixels. The DATA can be un-revealed;
// the buyer's knowledge cannot. They have already seen that part of the
// image, other buyers saw the image in that state, and a correct guess
// made using it stands. A partner choosing "re-hide" should know that is
// what they are choosing, so the panel says it plainly rather than
// implying the purchase can be made not to have happened.
import React, { useEffect, useState } from "react";
import { showToast } from "./toast.js";
import { siteDateTime } from "./siteTime.js";

function Frozen({ frozen, title, children }) {
  if (!frozen) return children;
  return (
    <fieldset disabled title={title} style={{ border: "none", padding: 0, margin: 0 }}>
      <div style={{ opacity: 0.55 }}>{children}</div>
    </fieldset>
  );
}

// PROVIDER NAMES, so this panel is not written for one of them.
//
// The wording used to say "Cashflows" and "Cashflows Go" outright, which
// is fine while there is one adapter and wrong the moment there are two.
// Adding a provider means adding a line here rather than hunting for
// prose that quietly became untrue.
const PROVIDER_NAMES = {
  cashflows: { name: "Cashflows", portal: "Cashflows Go" },
  paytriot: { name: "Paytriot", portal: "the Paytriot portal" },
};

function providerLabels(active) {
  const known = PROVIDER_NAMES[active];
  if (known) return known;
  // No provider configured, or one this panel has not been told about.
  // Generic wording is accurate for any of them.
  return { name: "your payment provider", portal: "your payment provider's portal" };
}

const OPTIONS = [
  {
    value: "none",
    label: "No refunds",
    description: "Refunds are not offered. No refund controls appear anywhere in the admin.",
  },
  {
    value: "money",
    label: "Refund the money only",
    description:
      "The buyer gets their money back and keeps the pixels they revealed. Simplest, and the buyer keeps what they paid for.",
  },
  {
    value: "rehide",
    label: "Refund and hide the pixels again",
    description:
      "The money goes back and that area of the image is hidden again. The buyer has already seen it, and so has anyone else who was looking — this un-reveals the data, not the knowledge.",
  },
  {
    value: "void",
    label: "Refund and void the entry entirely",
    description:
      "As above, and the spend is reversed — so any bonus guesses it earned go with it. If those guesses have already been used, the buyer will have used more than they now have.",
  },
];

export default function RefundPolicyPanel({ token, wsRequest, canEdit, modeStatus }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // DEMO ONLY, not "anything but Live".
  //
  // These are SETTINGS, and freezing them in Test mode meant switching to
  // Live to save a form and switching back to carry on testing — friction
  // with little behind it. Provider credentials already have separate
  // test and live slots, and the data on screen is gated by mode anyway.
  //
  // Demo stays frozen: root pulls it as a brake to show somebody the
  // product, and settings changing under a demonstration is a real
  // surprise. The server enforces the same rule in
  // assertSettingsEditable.
  const frozen = (modeStatus?.effectiveMode || "live") === "demo";
  const frozenTitle = "Leave Demo mode to manage this";
  const locked = !canEdit || frozen;

  const [activeProvider, setActiveProvider] = useState(null);

  const load = () => {
    wsRequest("admin.siteSettings.private", {}, token)
      .then((data) => setSettings(data))
      .catch((err) => {
        setLoadFailed(true);
        showToast(err.message || "Could not reach backend.", "error");
      });
    // Only for the wording below — which provider's portal to name. A
    // failure here is not worth surfacing: the generic wording is
    // accurate, just less specific.
    wsRequest("admin.paymentProvider.get", {}, token)
      .then((cfg) => setActiveProvider(cfg && cfg.active ? cfg.active : null))
      .catch(() => {});
  };

  useEffect(load, []);

  const apply = async (value) => {
    setBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", { refundPolicy: value }, token);
      showToast("Refund policy saved.");
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    } finally {
      setBusy(false);
    }
  };

  // loadFailed, not the old `error` state: the message itself now goes to
  // a toast, but this still has to stop showing "Loading…" for ever when
  // the fetch fails.
  if (!settings && !loadFailed) return <p>Loading…</p>;
  const current = settings?.refundPolicy || "none";
  const provider = providerLabels(activeProvider);

  return (
    <section style={{ maxWidth: 640, marginTop: 28 }}>
      <Frozen frozen={frozen} title={frozenTitle}>
        <h3 style={{ marginBottom: 4 }}>Refunds</h3>
        <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
          What a refund does to the purchase. This is your decision — it is your money and your
          terms.
        </p>

        {OPTIONS.map((o) => {
          const selected = current === o.value;
          return (
            <label
              key={o.value}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                padding: "10px 12px",
                border: "1px solid " + (selected ? "var(--accent-blue)" : "var(--border-color)"),
                borderRadius: 8,
                marginBottom: 8,
                cursor: locked ? "default" : "pointer",
              }}
            >
              <input
                type="radio"
                name="refund-policy"
                checked={selected}
                disabled={locked || busy}
                title={frozen ? frozenTitle : undefined}
                onChange={() => apply(o.value)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{o.label}</strong>
                <br />
                <span style={{ fontSize: 13, opacity: 0.75 }}>{o.description}</span>
              </span>
            </label>
          );
        })}

        {settings?.refundPolicyBy && (
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Set by {settings.refundPolicyBy}
            {settings.refundPolicyAt
              ? " on " + siteDateTime(settings.refundPolicyAt)
              : ""}
          </div>
        )}

        {/* SAYING WHAT IS NOT BUILT YET, rather than shipping a setting
            that silently governs nothing. */}
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            border: "1px solid var(--border-color)",
            borderRadius: 8,
            fontSize: 13,
            opacity: 0.85,
          }}
        >
          <strong>"Refund the money only" can now be issued from Finance &rarr;
          Transactions.</strong> A Refund button appears on each completed payment, and
          takes an amount so a partial refund is possible.
          <br />
          <br />
          <strong>The other two policies are not built.</strong> Hiding the pixels again,
          and reversing the spend, both need work beyond returning the money — so with
          either of those selected the button refuses rather than doing half of what the
          policy promises. Use {provider.portal} in the meantime, or set the policy to "Refund
          the money only".
          <br />
          <br />
          <strong>Never refund a payment that is already disputed.</strong> The cardholder is
          credited once for the chargeback and again for the refund, and you are debited twice.
          {activeProvider === "cashflows"
            ? " Cashflows notify disputes by email, so nothing here can detect one for you."
            : " Check how " + provider.name + " notifies you of disputes — if it is by email, nothing here can detect one for you."}
        </div>
      </Frozen>
    </section>
  );
}
